/**
 * The account's own self-service surface — profile, password, live sessions, API keys,
 * linked social identities, contact verification and push registration.
 *
 * All of it is `request()`, never `requestControl()`: identity comes from the JWT and a
 * company header here would be meaningless at best. Nothing in this file takes a
 * company id, which is the point — an account outlives any company it belongs to.
 */
import {request} from '@/shared/api';
import type {
  AccountProfile,
  AccountSessions,
  ApiKeyList,
  MintedApiKey,
  OAuthConnections,
  ProfileUpdate,
  PushPlatform,
  RegisteredPushToken,
  VerifyChannel,
} from './types';

export function fetchAccountProfile(): Promise<AccountProfile> {
  return request<AccountProfile>('/auth/profile');
}

export function updateAccountProfile(
  update: ProfileUpdate,
): Promise<AccountProfile> {
  return request<AccountProfile>('/auth/profile', {
    method: 'PUT',
    body: JSON.stringify(update),
  });
}

/** Change a password the account already has. */
export function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<void> {
  return request<void>('/auth/change-password', {
    method: 'POST',
    body: JSON.stringify({
      current_password: currentPassword,
      new_password: newPassword,
    }),
  });
}

/**
 * Set a first password — for an account created through a social login, where there is
 * no current password to prove. Guard the call with `profile.has_password`: on an
 * account that already has one this is not the right route.
 */
export function setPassword(newPassword: string): Promise<void> {
  return request<void>('/auth/set-password', {
    method: 'POST',
    body: JSON.stringify({new_password: newPassword}),
  });
}

/** Every refresh token still alive on this account — one row per signed-in device. */
export function fetchSessions(): Promise<AccountSessions> {
  return request<AccountSessions>('/auth/sessions');
}

/** Sign one session out. Revoking the CURRENT one logs this app out at its next call. */
export function revokeSessionById(sessionId: string): Promise<void> {
  return request<void>(`/auth/sessions/${encodeURIComponent(sessionId)}`, {
    method: 'DELETE',
  });
}

/**
 * Sign out everywhere, this device included — the response invalidates the token this
 * very call was made with, so the caller must drop its own session straight after.
 */
export function logoutEverywhere(): Promise<void> {
  return request<void>('/auth/logout-all', {method: 'POST'});
}

export function fetchApiKeys(): Promise<ApiKeyList> {
  return request<ApiKeyList>('/auth/api-keys');
}

/**
 * Mint an API key. `service` names what it is for and is required; `company_id` scopes
 * it to one company (omit for an account-level key). The secret comes back once.
 */
export function createApiKey(input: {
  name: string;
  service: string;
  companyId?: string;
  expiresInDays?: number;
  rateLimit?: number;
}): Promise<MintedApiKey> {
  const body: Record<string, unknown> = {
    name: input.name,
    service: input.service,
  };
  if (input.companyId) {
    body.company_id = input.companyId;
  }
  if (input.expiresInDays !== undefined) {
    body.expires_in_days = input.expiresInDays;
  }
  if (input.rateLimit !== undefined) {
    body.rate_limit = input.rateLimit;
  }
  return request<MintedApiKey>('/auth/api-keys', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function revokeApiKey(id: string): Promise<void> {
  return request<void>(`/auth/api-keys/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

export function fetchOAuthConnections(): Promise<OAuthConnections> {
  return request<OAuthConnections>('/auth/oauth/connections');
}

/**
 * Unlink a social identity. If it is the only way the account can sign in — no
 * password, one connection — this locks the user out; check `has_password` first.
 */
export function unlinkOAuthConnection(id: string): Promise<void> {
  return request<void>(`/auth/oauth/connections/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

/**
 * Send a verification code. `newValue` doubles as the "change my email/phone" path:
 * omitted verifies the value already on file, present verifies the new one and adopts
 * it on confirm.
 */
export function sendVerification(
  channel: VerifyChannel,
  newValue?: string,
): Promise<void> {
  const body: Record<string, unknown> = {channel};
  if (newValue) {
    body.new_value = newValue;
  }
  return request<void>('/auth/verify/send', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function confirmVerification(
  channel: VerifyChannel,
  code: string,
): Promise<void> {
  return request<void>('/auth/verify/confirm', {
    method: 'POST',
    body: JSON.stringify({channel, code}),
  });
}

/**
 * Register (or refresh) a push token for this device.
 *
 * See the note on `PushPlatform`: send a value from sv_notification's vocabulary, not
 * from the gateway DTO's comment. `deviceLabel` is free text for a "my devices" list
 * and carries no semantics.
 */
export async function registerPushToken(input: {
  token: string;
  platform: PushPlatform;
  deviceLabel: string;
}): Promise<RegisteredPushToken> {
  // The response shape is not pinned down by the spec, and the id it carries is the
  // ONLY way to revoke later: there is no route that lists an account's push tokens,
  // so an id not captured here is an id gone for good. Hence the tolerant read rather
  // than a declared DTO — and hence `id: null` being a supported outcome instead of a
  // parse failure.
  const body = await request<Record<string, unknown> | null>('/auth/push-token', {
    method: 'POST',
    body: JSON.stringify({
      token: input.token,
      platform: input.platform,
      device_label: input.deviceLabel,
    }),
  });
  const id = body?.token_id ?? body?.id ?? null;
  return {id: typeof id === 'string' && id ? id : null};
}

/**
 * ⚠️ The gateway handler for this takes only the path parameter — no headers, no
 * account — so it does not appear to check that the token belongs to the caller.
 * Whether sv_notification does is unverified. Pass only a token id this account was
 * shown; never one assembled from elsewhere.
 */
export function revokePushToken(tokenId: string): Promise<void> {
  return request<void>(`/auth/push-token/${encodeURIComponent(tokenId)}`, {
    method: 'DELETE',
  });
}
